"use client"

import { useEffect, useId, useRef, useState } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { ChevronDown, Play, Timer } from "lucide-react"
import { GameLibraryArtwork } from "./game-library-artwork"
import styles from "./game-type-card.module.css"

interface GameTypeCardProps {
  gameUrl: string
  categorySlug: string
  gameSlug: string
  name: string
  description: string | null
  entranceDelay?: number
}

const TIMER_PRESETS = [3, 5, 10, 15, 20, 30]

export function GameTypeCard({
  gameUrl,
  categorySlug,
  gameSlug,
  name,
  description,
  entranceDelay = 0,
}: GameTypeCardProps) {
  const router = useRouter()
  const timerPickerId = useId()
  const [timerOpen, setTimerOpen] = useState(false)
  const [selectedTimer, setSelectedTimer] = useState<number | null>(null)
  const [customInput, setCustomInput] = useState("")
  const pickerRef = useRef<HTMLDivElement>(null)
  const customInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!timerOpen) return

    const handlePointerDown = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setTimerOpen(false)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setTimerOpen(false)
      }
    }
    const focusFrame = requestAnimationFrame(() => {
      pickerRef.current
        ?.querySelector<HTMLElement>("[data-timer-option]")
        ?.focus()
    })

    document.addEventListener("mousedown", handlePointerDown)
    document.addEventListener("keydown", handleKeyDown)
    return () => {
      cancelAnimationFrame(focusFrame)
      document.removeEventListener("mousedown", handlePointerDown)
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [timerOpen])

  const buildUrl = (timerValue: number | null) => {
    if (timerValue === null) return gameUrl
    const separator = gameUrl.includes("?") ? "&" : "?"
    return `${gameUrl}${separator}timer=${timerValue}`
  }

  const handlePlay = () => {
    router.push(buildUrl(selectedTimer))
  }

  const handleTimerSelect = (value: number | null) => {
    setSelectedTimer(value)
    setTimerOpen(false)
  }

  const handleCustomSubmit = () => {
    const value = Number.parseInt(customInput)
    if (value >= 1 && value <= 120) {
      setSelectedTimer(value)
      setTimerOpen(false)
      setCustomInput("")
    }
  }
  const canonicalGameUrl = gameUrl.split("?")[0]

  return (
    <div
      className={styles.wrapper}
      ref={pickerRef}
      style={{ animationDelay: `${entranceDelay}ms` }}
    >
      <article className={styles.card}>
        <div className={styles.gameIcon} aria-hidden="true">
          <GameLibraryArtwork
            categorySlug={categorySlug}
            gameSlug={gameSlug}
          />
        </div>

        <div className={styles.copy}>
          <h3>
            <Link href={canonicalGameUrl}>{name}</Link>
          </h3>
          <p>
            {description ||
              `Compare real-world ${name.toLowerCase()} values and build your streak.`}
          </p>
        </div>

        <div className={styles.controls}>
          <button
            type="button"
            className={styles.timerButton}
            onClick={() => setTimerOpen((open) => !open)}
            aria-expanded={timerOpen}
            aria-controls={timerPickerId}
            data-selected={selectedTimer !== null ? "true" : undefined}
          >
            <Timer aria-hidden="true" />
            <span>{selectedTimer === null ? "Timer" : `${selectedTimer}s`}</span>
            <ChevronDown aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={handlePlay}
            className={styles.playButton}
            aria-label={`Play ${name}${
              selectedTimer === null
                ? ""
                : ` with ${selectedTimer} seconds per round`
            }`}
          >
            <span>Play</span>
            <span className={styles.playIcon} aria-hidden="true">
              <Play fill="currentColor" />
            </span>
          </button>
        </div>
      </article>

      {timerOpen ? (
        <section
          id={timerPickerId}
          data-timer-picker="open"
          aria-label={`Timer for ${name}`}
          className={styles.timerPanel}
        >
          <div className={styles.timerPanelHeading}>
            <div>
              <strong>Seconds per round</strong>
              <span>Choose a pace, or leave the timer off.</span>
            </div>
            {selectedTimer !== null ? (
              <button
                type="button"
                onClick={() => handleTimerSelect(null)}
              >
                Use no timer
              </button>
            ) : null}
          </div>

          <div className={styles.timerPresets}>
            {TIMER_PRESETS.map((value) => (
              <button
                type="button"
                data-timer-option
                key={value}
                onClick={() => handleTimerSelect(value)}
                data-selected={
                  selectedTimer === value ? "true" : undefined
                }
              >
                {value}s
              </button>
            ))}
          </div>

          <div className={styles.customTimer}>
            <label htmlFor={`${timerPickerId}-custom`}>
              Custom seconds
            </label>
            <div>
              <input
                ref={customInputRef}
                id={`${timerPickerId}-custom`}
                type="number"
                min={1}
                max={120}
                value={customInput}
                onChange={(event) => setCustomInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleCustomSubmit()
                  if (event.key === "Escape") {
                    setTimerOpen(false)
                    setCustomInput("")
                  }
                }}
                placeholder="1 to 120"
                aria-describedby={`${timerPickerId}-hint`}
              />
              <button
                type="button"
                onClick={handleCustomSubmit}
                disabled={
                  !customInput ||
                  Number.parseInt(customInput) < 1 ||
                  Number.parseInt(customInput) > 120
                }
              >
                Set timer
              </button>
            </div>
            <span id={`${timerPickerId}-hint`}>
              Enter a whole number from 1 to 120.
            </span>
          </div>

          {selectedTimer === null ? (
            <button
              type="button"
              className={styles.noTimerButton}
              onClick={() => handleTimerSelect(null)}
            >
              <Play aria-hidden="true" />
              Keep playing without a timer
            </button>
          ) : null}
        </section>
      ) : null}
    </div>
  )
}
